<template>
  <BaseModule
    v-bind="$props"
    class="container custom-form"
    data-module="custom-form"
    :style="{
      '--field-bg-color': fieldBackgroundColor,
    }"
  >
    <div class="content">
      <div class="wrapper">
        <div class="form-wrapper">
          <header
            v-if="title || subtitle"
            class="base-title-fontsize"
          >
            <component
              :is="titleTag"
              v-if="title"
              class="tit tit-g"
            >
              {{ title }}
            </component>
            <h3
              v-if="subtitle"
              class="sub"
            >
              {{ subtitle }}
            </h3>
          </header>
          <form
            class="form"
            novalidate
            @submit.prevent="handleSubmit"
          >
            <div
              v-for="field in formFields"
              :key="field.name"
              class="form-field"
              :class="fieldClasses(field)"
            >
              <FloatingLabel
                v-if="field.type !== 'checkbox' && field.type !== 'date'"
                :for-attr="field.id"
              >
                {{ `${field.label}${field.isOptional ? '' : '*'}` }}
              </FloatingLabel>

              <label
                v-if="field.type === 'checkbox'"
                class="checkbox-label"
                :for="field.id"
              >
                <Checkbox
                  :id="field.id"
                  v-model="data[field.name]"
                  color="var(--primary-color)"
                  @change="startValidate(field)"
                />
                <span>
                  {{ `${field.label}${field.isOptional ? '' : '*'}` }}
                </span>
              </label>

              <textarea
                v-else-if="field.type === 'textarea'"
                :id="field.id"
                v-model="data[field.name]"
                class="textarea"
                :name="field.name"
                rows="1"
                autocomplete="off"
                @blur="startValidate(field)"
              ></textarea>

              <select
                v-else-if="field.type === 'select'"
                :id="field.id"
                v-model="data[field.name]"
                :name="field.name"
                @blur="startValidate(field)"
              >
                <option
                  value=""
                  hidden
                ></option>
                <option
                  v-for="(option, opIndex) in field.options"
                  :key="opIndex"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>

              <DatePicker
                v-else-if="field.type === 'date'"
                v-model="data[field.name]"
                class="date-picker"
                mode="date"
                :model-config="{
                  type: 'string',
                  mask: 'DD/MM/YYYY',
                }"
                color="teal"
                :attributes="[
                  {
                    key: 'today',
                    highlight: {
                      color: 'teal',
                      fillMode: 'outline',
                    },
                    dates: new Date(),
                  },
                ]"
                @input="startValidate(field)"
              >
                <template #default="{ inputValue, inputEvents }">
                  <div class="datepicker-wrapper">
                    <FloatingLabel
                      v-model="data[field.name]"
                      :for-attr="field.id"
                    >
                      {{ `${field.label}${field.isOptional ? '' : '*'}` }}
                    </FloatingLabel>
                    <input
                      :id="field.id"
                      inputmode="numeric"
                      :value="inputValue"
                      autocomplete="off"
                      v-on="inputEvents"
                      @blur="startValidate(field)"
                    />
                    <span class="datepicker-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        width="16"
                        height="16"
                      >
                        <path
                          d="M1 4c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4zm2 2v12h14V6H3zm2-6h2v2H5V0zm8 0h2v2h-2V0zM5 9h2v2H5V9zm0 4h2v2H5v-2zm4-4h2v2H9V9zm0 4h2v2H9v-2zm4-4h2v2h-2V9zm0 4h2v2h-2v-2z"
                        ></path>
                      </svg>
                    </span>
                  </div>
                </template>
              </DatePicker>

              <ImaskInput
                v-else
                :id="field.id"
                :ref="field.id"
                v-model="data[field.name]"
                :inputmode="
                  field.type === 'number' || field.type === 'phone'
                    ? 'numeric'
                    : 'text'
                "
                :mask="getMask(field)"
                :name="field.name"
                type="text"
                @blur="startValidate(field)"
              />
            </div>

            <div
              class="form-field"
              :class="{ error: !isValidContactOptin }"
            >
              <label
                ref="consentText"
                class="optin-label"
                :for="optinId"
                :class="{ error: !isValidContactOptin }"
              >
                <Checkbox
                  :id="optinId"
                  v-model="contactOptin"
                  type="checkbox"
                  color="var(--primary-color)"
                  @change="startValidate('optin')"
                />
                <span v-html="consentText"></span>
              </label>
            </div>

            <div class="form__footer">
              <div
                class="form__message"
                :style="{
                  visibility: wasSent || editing ? 'visible' : 'hidden',
                }"
                :class="{
                  'form__message--success': wasSentWithSuccess,
                  'form__message--error': !wasSentWithSuccess,
                }"
              >
                <span
                  v-if="wasSentWithSuccess || editing"
                  v-html="successMessage"
                ></span>
                <span v-else>
                  {{ $t('errorMessage') }}
                </span>
              </div>
              <!-- popup exclusivo do Editor -->
              <div class="editor-form-disclaimer">
                <span class="slb-tooltip arrow-right-down">
                  Os envios de formulários são desativados no preview. Para
                  testar seu funcionamento, acesse sua página publicada.
                </span>
                <!-- botão desabilitado no Editor -->
                <button
                  type="submit"
                  class="cta"
                  :disabled="true"
                >
                  {{ submitButtonLabel }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div
          v-if="image && !isMobile"
          class="img-wrapper"
        >
          <LImage
            class="image"
            :src="image"
            :alt="altText"
          />
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { IMaskComponent } from 'vue-imask';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';

import isValidEmail from '@/validation/validators/email';
import isValidCpf from '@/validation/validators/cpf';
import isValidDate from '@/validation/validators/date';
import uniqueId from '@/helpers/uniqueId';
import base from '@/mixins/base';
import slugify from 'slugify';
import Checkbox from '@/components/site/Checkbox.vue';
import BaseModule from './generic/BaseModule.vue';
import FloatingLabel from '../components/site/FloatingLabel.vue';

export default {
  name: 'CustomForm',
  components: {
    BaseModule,
    Checkbox,
    DatePicker,
    FloatingLabel,
    ImaskInput: IMaskComponent,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    backgroundColor: {
      type: String,
      default: '#ffffff', // overrides default
    },
    fieldBackgroundColor: {
      type: String,
      default: '#ffffff',
    },
    image: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
    fields: {
      type: Array,
      default() {
        return [];
      },
    },
    emails: {
      type: Array,
      default() {
        return [];
      },
    },
    buttonText: {
      type: String,
      default: 'Enviar',
    },
    successMessage: {
      type: String,
      default: '',
    },
    consentText: {
      type: String,
      default: '',
    },
    enabledCRMIntegration: {
      type: Boolean,
      default: false,
    },
    idsCRMNovas: {
      type: Array,
      default() {
        return [];
      },
    },
    idsCRMExistentes: {
      type: Array,
      default() {
        return [];
      },
    },
    useExclusiveContent: {
      type: Boolean,
      default: false,
    },
    exclusiveContent: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    paddingTop: {
      type: Number,
      default: 52, // overrides default
    },
    paddingBottom: {
      type: Number,
      default: 52, // overrides default
    },
  },
  data() {
    return {
      data: {},
      isMobile: false,
      mobileMaxWidth: 768,
      contactOptin: false,
      optinId: null,
      isSending: false,
      isDirty: [],
      wasSent: false,
      wasSentWithSuccess: true,
    };
  },
  computed: {
    formFields() {
      let count = 1;

      const normalizeFieldName = (label) => {
        const slug = slugify(label, { lower: true, strict: true });
        return slug === 'e-mail' ? 'email' : slug;
      };

      const fields = [
        {
          type: 'text',
          label: this.$t('placeHolderName'),
          name: 'name',
        },
        {
          type: 'text',
          label: this.$t('placeHolderLastName'),
          name: 'surname',
        },
        {
          type: 'email',
          label: 'E-mail',
          name: 'email',
        },
        ...this.fields,
      ];

      return fields.map((field) => {
        const id =
          field.type === 'checkbox'
            ? `checkbox-${count}-${this.instanceId}`
            : `${normalizeFieldName(field.label)}-${this.instanceId}`;
        const name =
          field.name || field.type === 'checkbox'
            ? `checkbox-${count}`
            : normalizeFieldName(field.label);

        if (field.type === 'checkbox') {
          count += 1;
        }

        return {
          ...field,
          id,
          name,
        };
      });
    },
    submitButtonLabel() {
      if (this.isSending) {
        return this.$t('sendingText');
      }

      return this.buttonText;
    },
    idsCRMNovasValid() {
      return this.idsCRMNovas.filter(this.isValidCrmGroupId);
    },
    idsCRMExistentesValid() {
      return this.idsCRMExistentes.filter(this.isValidCrmGroupId);
    },
    editing() {
      return typeof this.$parent === 'undefined';
    },
    requiredFields() {
      const fields = [];

      this.formFields.forEach((field) => {
        if (!field.isOptional) {
          fields.push(field);
        }
      });
      return fields;
    },
    isValidContactOptin() {
      return !this.isDirty.includes('optin') || this.contactOptin;
    },
    exclusivePath() {
      const path = this.exclusiveContent;
      const target = path.openInNewTab ? '_blank' : '_self';

      if (path.type === 'externalLink' || path.type === 'file') {
        return {
          href: path.value,
          target,
        };
      }

      const page = this.pages.find(({ id }) => id === path.value);
      const { language = 'pt', urlPath = 'nao-encontrado' } = page || {};
      const url = this.isHome(urlPath);
      const href = language !== 'pt' ? `${language}/${url}` : `/${url}`;

      return {
        href,
        target,
      };
    },
    ...mapGetters(['settings']),
    ...mapState(['pages', 'activePageId']),
  },
  created() {
    this.formFields.forEach((field) => {
      this.data[field.name] = field.type === 'checkbox' ? false : '';
    });
  },
  mounted() {
    this.optinId = uniqueId();
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.handleTextareaHeight();
  },
  methods: {
    isHome(urlPath) {
      return urlPath === '_' ? '' : urlPath;
    },
    handleTextareaHeight() {
      const allTextarea = this.$el.querySelectorAll('textarea');
      allTextarea.forEach((textarea) => {
        textarea.addEventListener('input', () => {
          // eslint-disable-next-line no-param-reassign
          textarea.style.height = 'auto';
          // eslint-disable-next-line no-param-reassign
          textarea.style.height = `${textarea.scrollHeight}px`;
        });
      });
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    fieldClasses(field) {
      const classes = [];
      if (this.isDirty.includes(field.name)) {
        classes.push(this.isValid(field) ? 'success' : 'error');
      }
      return classes;
    },
    isValid(field) {
      switch (field.type) {
        case 'email':
          if (this.data[field.name]) {
            return isValidEmail(this.data[field.name]);
          }
          return field.isOptional;
        case 'cpf':
          if (this.data[field.name]) {
            return isValidCpf(this.data[field.name]);
          }
          return field.isOptional;
        case 'date':
          if (this.data[field.name]) {
            return isValidDate(this.data[field.name]);
          }
          return field.isOptional;
        case 'phone':
          if (this.data[field.name]) {
            return this.data[field.name]?.replace(/\D/g, '').length >= 10;
          }
          return field.isOptional;
        default:
          return field.isOptional || !!this.data[field.name];
      }
    },
    isValidCrmGroupId(id) {
      return typeof id === 'string' && /^\d+$/.test(id.trim());
    },
    isFormValid() {
      return (
        this.requiredFields.every((field) => this.isValid(field)) &&
        this.isValidContactOptin
      );
    },
    async handleSubmit() {
      this.startValidate('optin');
      this.formFields.forEach((field) => {
        this.startValidate(field);
      });

      if (this.isFormValid()) {
        this.isSending = true;

        try {
          await this.sendEmail();

          if (this.useExclusiveContent) {
            setTimeout(() => {
              const { href, target } = this.exclusivePath;
              window.open(href, target);
            }, 1000);
          }
        } catch (error) {
          this.wasSentWithSuccess = false;
        }

        this.wasSent = true;
        this.isSending = false;

        setTimeout(() => {
          this.wasSent = false;
        }, 4000);
      }
    },
    async sendEmail() {
      const isIntegrated = this.enabledCRMIntegration;
      const body = {
        to: this.emails,
        subject: `Novo contato via site de ${this.data.name}`,
        sendEmail: true,
        integrated: isIntegrated,
        id: this.activePageId,
        url: window.location.href,
        data: {},
      };
      Object.entries(this.data).forEach(([key, value]) => {
        const fieldConfig = this.formFields.find((field) => field.name === key);
        body.data[key] = {
          value,
          label: fieldConfig.label,
        };
      });

      body.data.consentText = {
        value: this.$refs.consentText.textContent.trim(),
        label: 'Texto de consentimento',
      };

      body.data.acceptedConsent = {
        value: this.contactOptin,
        label: 'Aceitou o consentimento',
      };

      if (isIntegrated) {
        body.crmData = {
          groups: this.idsCRMNovasValid.map((id) => ({
            Numgrupo: Number(id),
          })),
          alternativeGroups: this.idsCRMExistentesValid.map((id) => ({
            Numgrupo: Number(id),
          })),
        };
      }

      const { apiBaseUrl, customer } = this.$store.state;

      fetch(`${apiBaseUrl}contact/${customer}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(body),
      })
        .then((res) => {
          this.wasSentWithSuccess = res.status === 200;
        })
        .catch(() => {
          this.wasSentWithSuccess = false;
        })
        .finally(() => {
          this.isSending = false;
          this.wasSent = true;
        });
    },
    startValidate(field) {
      if (field === 'optin') {
        this.isDirty.push(field);
        return;
      }

      if (
        this.data[field.name] ||
        !field.isOptional ||
        field.type === 'checkbox'
      ) {
        this.isDirty.push(field.name);
      }
    },
    getMask({ type }) {
      switch (type) {
        case 'date':
          return '00/00/0000';
        case 'number':
          return /^\d+$/;
        case 'cpf':
          return '000.000.000-00';
        case 'phone':
          return '(00) 00000-0000';
        default:
          return /^.+$/;
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            label: 'Textos',
            fields: [
              {
                name: 'consentText',
                type: 'Html',
                maxlength: 200,
                label: 'Texto de consentimento',
              },
              {
                name: 'successMessage',
                type: 'ShortText',
                label: 'Mensagem de sucesso',
                maxlength: 60,
              },
              {
                name: 'buttonText',
                type: 'ShortText',
                label: 'Texto do botão',
              },
            ],
            toRemove: ['text'],
          },
          {
            name: 'images',
            label: 'Imagens',
            fields: [
              {
                name: 'image',
                type: 'ImageFile',
                label: 'Imagem',
                enableRemoval: true,
                helpText: 'Disponível apenas em desktop',
              },
              {
                name: 'altText',
                type: 'ShortText',
                label: 'Texto alternativo',
              },
            ],
          },
          {
            name: 'fields',
            label: 'Campos do formulário',
            fields: [
              {
                name: 'fields',
                type: 'List',
                itemLabel: 'Campo {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar campo',
                fields: [
                  {
                    name: 'type',
                    type: 'VueSelect',
                    label: 'Tipo',
                    default: 'text',
                    options: [
                      {
                        label: 'Texto livre',
                        value: 'text',
                      },
                      {
                        label: 'E-mail',
                        value: 'email',
                      },
                      {
                        label: 'Telefone',
                        value: 'phone',
                      },
                      {
                        label: 'CPF',
                        value: 'cpf',
                      },
                      {
                        label: 'Numérico',
                        value: 'number',
                      },
                      {
                        label: 'Combo (select)',
                        value: 'select',
                      },
                      {
                        label: 'Data',
                        value: 'date',
                      },
                      {
                        label: 'Área de texto',
                        value: 'textarea',
                      },
                      {
                        label: 'Checkbox',
                        value: 'checkbox',
                      },
                    ],
                  },
                  {
                    name: 'options',
                    type: 'Tags',
                    label: 'Opções',
                    isEnabled() {
                      return this.type === 'select';
                    },
                  },
                  {
                    name: 'label',
                    type: 'ShortText',
                    label: 'Nome do campo',
                    default: 'Campo customizado',
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message: 'Dê um nome para o campo',
                      },
                    },
                  },
                  {
                    name: 'isOptional',
                    type: 'Checkbox',
                    label: 'Preenchimento opcional',
                  },
                ],
              },
            ],
          },
          {
            name: 'exclusiveFormContent',
            label: 'Link após envio do formulário',
            fields: [
              {
                name: 'useExclusiveContent',
                type: 'Checkbox',
                label: 'Ativar link pós envio',
                helpText:
                  'Ao ativar, o usuário será direcionado para o link configurado após o envio bem sucedido do formulário.',
              },
              {
                name: 'exclusiveContent',
                type: 'Url',
                availableTypes: ['externalLink', 'page', 'file'],
                isEnabled({ useExclusiveContent }) {
                  return useExclusiveContent;
                },
              },
            ],
          },
          {
            name: 'emails',
            label: 'Destinatários',
            fields: [
              {
                name: 'emails',
                type: 'Tags',
                label: 'Destinatários',
                helpText: `
                      Os destinatários precisam estar cadastrados na aba de Configurações.
                      <a href="/settings#email" target="_blank">
                      <b>Clique aqui para ver a lista completa</b></a>.
                    `,
                whitelistFromCustomerSetting: 'emails',
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um destinatário para o recebimento dos e-mails',
                  },
                },
              },
            ],
          },
          {
            name: 'integrations',
            label: 'Integrações',
            isEnabled({ hasCRMIntegration }) {
              return hasCRMIntegration;
            },
            fields: [
              {
                helpText() {
                  if (this.enabledCRMIntegration) {
                    return '<span class="warning">AVISO: Os dados dos campos extras não são integrados com o CRM</span>';
                  }
                  return '';
                },
                name: 'enabledCRMIntegration',
                type: 'Checkbox',
                label: 'Integrar com o CRM',
                isEnabled({ hasCRMIntegration }) {
                  return hasCRMIntegration;
                },
              },
              {
                name: 'idsCRMNovas',
                type: 'Tags',
                label: 'IDs - Pessoas novas',
                helpText:
                  'Grupos de leads que se cadastraram pela primeira vez',
                pattern: /^\d+$/,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um grupo de CRM para pessoas novas',
                  },
                },
                isEnabled({ enabledCRMIntegration }) {
                  return enabledCRMIntegration;
                },
              },
              {
                name: 'idsCRMExistentes',
                type: 'Tags',
                label: 'IDs - Pessoas existentes',
                helpText: 'Grupos de leads que se cadastraram novamente',
                pattern: /^\d+$/,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um grupo de CRM para pessoas existentes',
                  },
                },
                isEnabled({ enabledCRMIntegration }) {
                  return enabledCRMIntegration;
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'fieldBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo dos campos',
                after: 'textColor',
              },
            ],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        placeHolderName: 'Nome',
        placeHolderLastName: 'Sobrenome',
        errorMessage: 'Ops! Algo errado aconteceu ao enviar seu contato.',
        sendingText: 'Enviando...',
      },
      en: {
        placeHolderName: 'Name',
        placeHolderLastName: 'Last name',
        errorMessage:
          'Oops! Something wrong happened when sending your contact.',
        sendingText: 'Sending...',
      },
      es: {
        placeHolderName: 'Nombre',
        placeHolderLastName: 'Apellido',
        errorMessage: '¡UPS! Ocurrió algo incorrecto al enviar su contacto.',
        sendingText: 'Enviando...',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.custom-form {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    max-width: 1280px;

    .wrapper {
      padding: 0 32px;
      position: relative;
      display: flex;
      align-items: stretch;
      justify-content: space-between;

      .form-wrapper,
      .img-wrapper {
        width: 100%;
        max-width: 576px;
      }

      .form-wrapper {
        padding: 12px 48px;
        margin: 0 auto;

        header {
          text-align: left;
        }

        .form {
          width: 100%;

          .tit-m {
            font-weight: $lb-light;
          }

          .form-field {
            text-align: left;
            position: relative;
            width: 100%;
            color: var(--text-color, $gray-900);

            + .form-field {
              margin-top: 24px;
            }

            label {
              margin-bottom: 6px;
              color: inherit;
            }

            input,
            select,
            textarea {
              border: 1px solid $lb-medium-gray;
              background-color: var(--field-bg-color, $lb-white);
              border-radius: 8px;
              width: 100%;
              font-style: normal;
              font-size: $size-normal;
              line-height: 24px;
              outline: none;
              padding: 20px 14px 6px;
              overflow: hidden;
              color: inherit;
              resize: none;

              &:focus {
                border-color: $lb-black;
              }

              /* Change autocomplete styles in WebKit */
              &:-webkit-autofill,
              &:-webkit-autofill:hover,
              &:-webkit-autofill:focus,
              &:-webkit-autofill:active {
                box-shadow: 0 0 0 30px var(--field-bg-color, $lb-white) inset !important;
                -webkit-box-shadow: 0 0 0 30px var(--field-bg-color, $lb-white)
                  inset !important;
                -webkit-text-fill-color: var(
                  --text-color,
                  $gray-900
                ) !important;
              }
            }

            .checkbox-label,
            .optin-label {
              display: flex;
              align-items: center;
              cursor: pointer;
              color: inherit;

              .checkbox {
                flex-shrink: 0;
              }

              span {
                margin: 0 0 0 12px;
                text-align: start;

                ::v-deep {
                  a {
                    color: inherit;
                    text-decoration: underline;

                    &:hover {
                      opacity: 0.8;
                    }
                  }
                }
              }
            }

            .date-picker {
              width: 100%;
              display: block;

              .vc-popover-content-wrapper {
                z-index: 1000;
              }

              .datepicker-wrapper {
                height: 100%;
                display: flex;
                align-items: stretch;

                input {
                  border-radius: 8px 0 0 8px;
                  border-right: none;

                  &:focus {
                    border-color: $crm-blue-primary;
                  }
                }

                .datepicker-icon {
                  padding: 0 16px;
                  border-radius: 0 8px 8px 0;
                  border: 1px solid $lb-medium-gray;
                  background-color: var(--field-bg-color, $lb-white);
                  border-left: none;

                  svg {
                    display: block;
                    width: 16px;
                    height: 100%;
                    fill: var(--text-color, $gray-900);
                  }
                }
              }
            }

            &.error {
              label {
                color: $lb-red;
                font-weight: $lb-semibold;

                input {
                  color: $lb-red;
                }
              }

              input,
              select,
              textarea {
                border-color: $lb-red !important;
                color: $lb-red;
                font-weight: $lb-semibold;

                option {
                  color: #000;
                }
              }

              .datepicker-icon {
                border-color: $lb-red !important;

                svg {
                  fill: $lb-red !important;
                }
              }
            }
          }

          &__footer {
            display: flex;
            align-items: center;
            flex-direction: column;
            margin-top: 32px;

            .editor-form-disclaimer {
              width: 100%;
              text-align: center;
            }

            .cta {
              width: 100%;
              border-radius: 8px;
              overflow: hidden;
            }
          }

          &__message {
            margin-bottom: 20px;
            border: 1px solid $lb-black;
            padding: 0.5em 1em;
            display: inline-block;
            text-align: left;

            &--success {
              background-color: #d9ffd9;
              border-color: green;
              border-radius: 8px;
              color: green;
              white-space: pre-line;
            }

            &--error {
              background-color: #fde6eb;
              border-color: crimson;
              border-radius: 8px;
              color: crimson;
            }
          }
        }
      }

      .img-wrapper {
        overflow: hidden;
        max-height: 100vh;
        position: sticky;
        top: 0;
      }
    }
  }

  @include media-breakpoint-down(lg) {
    .content {
      .wrapper {
        padding: 0;
      }
    }
  }

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .content {
      .wrapper {
        .form-wrapper {
          padding: 12px 20px;
        }
      }
    }
  }
}
</style>
